export type PplCtaDimensions = {
  engagement_component?: string;
  engagement_details?: string;
  engagement_destination?: string;
};

const PPL_CTA_DIMENSIONS_CONSUMED_KEY = '__gdmPplCtaDimensionsConsumed';
const LAST_PPL_CTA_EVENT_KEY = '__gdmLasPplCtaEvent';
const LAST_PPL_CTA_EVENT_CAPTURED_AT_KEY = '__gdmLasPplCtaEventCapturedAt';
const CONSUMED_PPL_CTA_EVENT_SIGNATURE_KEY =
  '__gdmConsumedPplCtaEventSignature';
const PPL_CTA_CAPTURE_INSTALLED_KEY = '__gdmLasPplCtaCaptureInstalled';
const SABX_EVENT_PREFIX = /^sabx - /;
const PPL_DESTINATION_PATTERN = /(^|[_-])ppl([_-]|$)/i;
const RECENT_CAPTURE_MS = 1000;

type PplCtaDataLayerEvent = Record<string, unknown> & {
  [PPL_CTA_DIMENSIONS_CONSUMED_KEY]?: boolean;
};

type PplCtaTrackingWindow = typeof window & {
  dataLayer?: unknown[];
  __GDM_EVENTS__?: Record<string, unknown>;
  [LAST_PPL_CTA_EVENT_KEY]?: PplCtaDataLayerEvent;
  [LAST_PPL_CTA_EVENT_CAPTURED_AT_KEY]?: number;
  [CONSUMED_PPL_CTA_EVENT_SIGNATURE_KEY]?: string;
  [PPL_CTA_CAPTURE_INSTALLED_KEY]?: boolean;
};

type PplCtaCaptureEvent =
  | Pick<Event, 'target'>
  | { target?: EventTarget | null };

type PplCtaTrackingPayload = {
  tracking?: string;
  queryParams?: Record<string, string>;
  [key: string]: unknown;
};

const getBrowserWindow = () => window as PplCtaTrackingWindow;

const isObjectRecord = (value: unknown): value is PplCtaDataLayerEvent =>
  Boolean(value && typeof value === 'object');

const getDataLayerEventName = (event: PplCtaDataLayerEvent) => {
  if (typeof event.eventName === 'string') {
    return event.eventName;
  }

  if (typeof event.event_name === 'string') {
    return event.event_name;
  }

  if (typeof event.name === 'string') {
    return event.name;
  }

  if (typeof event.event === 'string') {
    return event.event.replace(SABX_EVENT_PREFIX, '');
  }

  return '';
};

const getStringField = (event: PplCtaDataLayerEvent, ...keys: string[]) => {
  for (const key of keys) {
    const value = event[key];

    if (typeof value === 'string') {
      return value;
    }
  }

  return undefined;
};

const getPplCtaDimensionsFromEvent = (
  event: PplCtaDataLayerEvent
): PplCtaDimensions => ({
  engagement_component: getStringField(
    event,
    'engagement_component',
    'engagementComponent'
  ),
  engagement_details: getStringField(
    event,
    'engagement_details',
    'engagementDetails'
  ),
  engagement_destination: getStringField(
    event,
    'engagement_destination',
    'engagementDestination'
  ),
});

const hasPplCtaDimensions = (dimensions: PplCtaDimensions) =>
  Object.values(dimensions).some(Boolean);

const getDefinedPplCtaDimensions = (dimensions: PplCtaDimensions) => {
  const definedDimensions: Record<string, string> = {};

  for (const [key, value] of Object.entries(dimensions)) {
    if (value) {
      definedDimensions[key] = value;
    }
  }

  return definedDimensions;
};

const createPplCtaEventSignature = (event: PplCtaDataLayerEvent) => {
  const dimensions = getPplCtaDimensionsFromEvent(event);

  return [
    getDataLayerEventName(event),
    dimensions.engagement_component,
    dimensions.engagement_details,
    dimensions.engagement_destination,
  ].join('|');
};

const markPplCtaDimensionsConsumed = (event: PplCtaDataLayerEvent) => {
  Object.defineProperty(event, PPL_CTA_DIMENSIONS_CONSUMED_KEY, {
    configurable: true,
    enumerable: false,
    value: true,
    writable: true,
  });
};

const isPplCtaEvent = (event: unknown): event is PplCtaDataLayerEvent => {
  if (!isObjectRecord(event)) {
    return false;
  }

  if (getDataLayerEventName(event) === 'ppl_cta') {
    return true;
  }

  const engagementDestination = getStringField(
    event,
    'engagement_destination',
    'engagementDestination'
  );

  return Boolean(
    engagementDestination && PPL_DESTINATION_PATTERN.test(engagementDestination)
  );
};

const getEventDataFromTarget = (
  target: EventTarget | null | undefined
): PplCtaDataLayerEvent | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  if (typeof Element === 'undefined' || !(target instanceof Element)) {
    return undefined;
  }

  const element = target.closest<HTMLElement>(
    '[data-evt-name]:not(.prevent-event), [data-evt-id]:not(.prevent-event)'
  );
  const { evtId, evtFallback } = element?.dataset || {};
  const browserWindow = getBrowserWindow();
  const registeredEvent = evtId
    ? browserWindow.__GDM_EVENTS__?.[evtId]
    : undefined;

  if (isObjectRecord(registeredEvent)) {
    return registeredEvent;
  }

  if (!evtFallback) {
    return undefined;
  }

  try {
    const fallbackEvent = JSON.parse(browserWindow.atob(evtFallback));
    return isObjectRecord(fallbackEvent) ? fallbackEvent : undefined;
  } catch (_error) {
    return undefined;
  }
};

export const capturePplCtaEventData = (eventData: unknown) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!isPplCtaEvent(eventData)) {
    return;
  }

  delete eventData[PPL_CTA_DIMENSIONS_CONSUMED_KEY];
  const browserWindow = getBrowserWindow();
  browserWindow[LAST_PPL_CTA_EVENT_KEY] = eventData;
  browserWindow[LAST_PPL_CTA_EVENT_CAPTURED_AT_KEY] = Date.now();
  browserWindow[CONSUMED_PPL_CTA_EVENT_SIGNATURE_KEY] = undefined;
};

export const capturePplCtaEvent = (event: PplCtaCaptureEvent) => {
  capturePplCtaEventData(getEventDataFromTarget(event?.target));
};

export const installPplCtaTrackingCapture = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const browserWindow = getBrowserWindow();

  if (browserWindow[PPL_CTA_CAPTURE_INSTALLED_KEY]) {
    return;
  }

  browserWindow.addEventListener('mousedown', capturePplCtaEvent, true);
  browserWindow.addEventListener('click', capturePplCtaEvent, true);
  browserWindow[PPL_CTA_CAPTURE_INSTALLED_KEY] = true;
};

const getCapturedPplCtaDimensions = (): {
  found: boolean;
  dimensions: PplCtaDimensions;
} => {
  const browserWindow = getBrowserWindow();
  const event = browserWindow[LAST_PPL_CTA_EVENT_KEY];
  const capturedAt = browserWindow[LAST_PPL_CTA_EVENT_CAPTURED_AT_KEY];

  if (
    !(event && capturedAt) ||
    Date.now() - capturedAt > RECENT_CAPTURE_MS ||
    !isPplCtaEvent(event)
  ) {
    return { found: false, dimensions: {} };
  }

  const signature = createPplCtaEventSignature(event);

  if (
    event[PPL_CTA_DIMENSIONS_CONSUMED_KEY] ||
    browserWindow[CONSUMED_PPL_CTA_EVENT_SIGNATURE_KEY] === signature
  ) {
    return { found: true, dimensions: {} };
  }

  markPplCtaDimensionsConsumed(event);
  browserWindow[CONSUMED_PPL_CTA_EVENT_SIGNATURE_KEY] = signature;

  const dimensions = getPplCtaDimensionsFromEvent(event);
  return {
    found: true,
    dimensions: hasPplCtaDimensions(dimensions) ? dimensions : {},
  };
};

const getLatestDataLayerPplCtaDimensions = (): PplCtaDimensions => {
  const browserWindow = getBrowserWindow();

  if (!Array.isArray(browserWindow.dataLayer)) {
    return {};
  }

  for (let index = browserWindow.dataLayer.length - 1; index >= 0; index--) {
    const event = browserWindow.dataLayer[index];

    if (!isPplCtaEvent(event)) {
      continue;
    }

    const signature = createPplCtaEventSignature(event);

    if (
      event[PPL_CTA_DIMENSIONS_CONSUMED_KEY] ||
      browserWindow[CONSUMED_PPL_CTA_EVENT_SIGNATURE_KEY] === signature
    ) {
      return {};
    }

    markPplCtaDimensionsConsumed(event);
    browserWindow[CONSUMED_PPL_CTA_EVENT_SIGNATURE_KEY] = signature;

    const dimensions = getPplCtaDimensionsFromEvent(event);
    return hasPplCtaDimensions(dimensions) ? dimensions : {};
  }

  return {};
};

const getLatestPplCtaDimensions = (): PplCtaDimensions => {
  if (typeof window === 'undefined') {
    return {};
  }

  const captured = getCapturedPplCtaDimensions();

  if (captured.found) {
    return captured.dimensions;
  }

  return getLatestDataLayerPplCtaDimensions();
};

const appendTrackingParam = (
  params: string[],
  key: string,
  value: string | undefined
) => {
  if (value) {
    params.push(`${key}=${encodeURIComponent(value)}`);
  }
};

const appendDimensionsToTracking = (
  tracking = '',
  dimensions: PplCtaDimensions = {}
) => {
  const params = tracking ? [tracking] : [];

  appendTrackingParam(
    params,
    'engagement_component',
    dimensions.engagement_component
  );
  appendTrackingParam(
    params,
    'engagement_details',
    dimensions.engagement_details
  );
  appendTrackingParam(
    params,
    'engagement_destination',
    dimensions.engagement_destination
  );

  return params.join('&');
};

export const appendPplCtaDimensionsToTracking = (tracking = '') =>
  appendDimensionsToTracking(tracking, getLatestPplCtaDimensions());

export const appendExitIntentEngagementComponentToTracking = (tracking = '') =>
  appendDimensionsToTracking(tracking, {
    engagement_component: 'exit-intent',
  });

export const getEmbeddedFormQueryParams = () => ({
  engagement_component: 'embedded',
});

export const getPplCtaQueryParams = () =>
  getDefinedPplCtaDimensions(getLatestPplCtaDimensions());

export const withExitIntentEngagementComponent = <
  Payload extends PplCtaTrackingPayload,
>(
  payload: Payload
): Payload & PplCtaTrackingPayload =>
  ({
    ...payload,
    tracking: appendExitIntentEngagementComponentToTracking(payload.tracking),
  }) as Payload & PplCtaTrackingPayload;

export const withEmbeddedEngagementComponent = <
  Payload extends PplCtaTrackingPayload,
>(
  payload: Payload
): Payload & PplCtaTrackingPayload =>
  ({
    ...payload,
    queryParams: {
      ...(payload.queryParams || {}),
      ...getEmbeddedFormQueryParams(),
    },
  }) as Payload & PplCtaTrackingPayload;

export const withPplCtaTracking = <Payload extends PplCtaTrackingPayload>(
  payload: Payload
): Payload & PplCtaTrackingPayload => {
  const dimensions = getLatestPplCtaDimensions();

  if (!hasPplCtaDimensions(dimensions)) {
    return payload;
  }

  return {
    ...payload,
    tracking: appendDimensionsToTracking(payload.tracking, dimensions),
  } as Payload;
};
